import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';

import StickyFooter from '../components/StickyFooter';
import { uploadData, getUrl, list } from 'aws-amplify/storage';

interface Partner {
  id: string;
  name: string;
  logoPath: string;
  website?: string;
  person?: string;
  email?: string;
  description?: string;
  isActive?: boolean;
  displayOrder?: number;
  createdAt: string;
  updatedAt: string;
}

interface AdminPartnerEditorProps {
  selectedPartnerId?: string; // If provided, edit mode; if not, create mode
  onBack?: () => void;
  partnerId?: string; // If provided, edit mode; if not, create mode
  onSave?: (partner: Partner) => void;
  onCancel?: () => void;
}

const client = generateClient<Schema>({ authMode: 'userPool' });

const AdminPartnerEditor: React.FC<AdminPartnerEditorProps> = ({
  selectedPartnerId: propSelectedPartnerId,
  onBack,
  partnerId,
  onSave,
  onCancel
}) => {
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>(propSelectedPartnerId || partnerId || '');
  const [availablePartners, setAvailablePartners] = useState<{ id: string; name: string }[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    logoPath: '',
    website: '',
    person: '',
    email: '',
    description: '',
    isActive: true,
    displayOrder: 0
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoUploading, setLogoUploading] = useState<boolean>(false);
  const [existingLogoItems, setExistingLogoItems] = useState<{ key: string; label: string }[]>([]);
  const [loadingExistingLogos, setLoadingExistingLogos] = useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const isEditMode = Boolean(selectedPartnerId);

  useEffect(() => {
    loadAvailablePartners();
  }, []);

  useEffect(() => {
    if (propSelectedPartnerId) {
      setSelectedPartnerId(propSelectedPartnerId);
      loadPartner(propSelectedPartnerId);
    }
  }, [propSelectedPartnerId]);

  useEffect(() => {
    if (isEditMode && selectedPartnerId) {
      loadPartner(selectedPartnerId);
    }
  }, [selectedPartnerId, isEditMode]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  // Resolve logoPath to preview URL if it looks like an S3 key
  useEffect(() => {
    (async () => {
      const path = formData.logoPath || '';
      if (!path) {
        setLogoPreview('');
        return;
      }
      try {
        if (path.startsWith('http') || path.startsWith('/')) {
          setLogoPreview(path);
        } else {
          const publicPath = path.startsWith('public/') ? path : `public/${path}`;
          const { url } = await getUrl({ path: publicPath });
          setLogoPreview(url.toString());
        }
      } catch (e) {
        // fallback to legacy /assets path
        setLogoPreview(`/assets/images/${path}`);
      }
    })();
  }, [formData.logoPath]);

  // Load existing partner logos from S3 (public/partners/...)
  const loadExistingPartnerLogos = async () => {
    try {
      setLoadingExistingLogos(true);
      const items: { key: string; label: string }[] = [];
      
      // Only load logos for this specific partner if name exists
      if (!formData.name || formData.name.trim() === '') {
        console.log('No partner name set, skipping logo list');
        setExistingLogoItems([]);
        return;
      }
      
      // Create partner-specific folder path
      const safeSlug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9\-]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      // Load from partner-specific subfolder (new structure)
      const partnerPath = `public/partners/${safeSlug}/`;
      console.log(`📂 Loading logos for partner from: ${partnerPath}`);
      
      try {
        const { items: listed } = await list({ path: partnerPath });
        for (const it of listed || []) {
          const path = (it.path || '').replace(/^\//, '');
          if (!path || path.endsWith('/')) continue;
          const displayKey = path.startsWith('public/') ? path.substring('public/'.length) : path;
          const fileName = displayKey.split('/').pop() || displayKey;
          items.push({ key: displayKey, label: `📁 ${fileName}` });
        }
      } catch (e) {
        console.log(`No subfolder found for ${safeSlug}, checking root partners folder`);
      }
      
      // Also check root partners folder for legacy logos
      try {
        const { items: rootListed } = await list({ path: 'public/partners/' });
        for (const it of rootListed || []) {
          const path = (it.path || '').replace(/^\//, '');
          if (!path || path.endsWith('/')) continue;
          
          // Skip if it's in a subdirectory (already handled above)
          const displayKey = path.startsWith('public/') ? path.substring('public/'.length) : path;
          const pathParts = displayKey.split('/');
          if (pathParts.length > 2) continue; // Skip partners/subfolder/file.svg
          
          // Check if filename matches partner name
          const fileName = displayKey.split('/').pop() || displayKey;
          const fileSlug = fileName.toLowerCase().replace(/\.(svg|png|jpg|jpeg)$/i, '');
          
          if (fileSlug.includes(safeSlug) || safeSlug.includes(fileSlug)) {
            items.push({ key: displayKey, label: `${fileName}` });
          }
        }
      } catch (e) {
        console.log('Could not check root partners folder');
      }
      
      // If current logoPath exists and isn't in the list, add it
      if (formData.logoPath && !items.find(item => item.key === formData.logoPath)) {
        const currentFileName = formData.logoPath.split('/').pop() || formData.logoPath;
        items.push({ 
          key: formData.logoPath, 
          label: `✓ ${currentFileName} (current)` 
        });
      }
      
      items.sort((a, b) => a.label.localeCompare(b.label));
      setExistingLogoItems(items);
      console.log(`✅ Found ${items.length} logo(s) for partner ${formData.name}`);
    } catch (e) {
      console.warn('Failed to list partner logos:', e);
      setExistingLogoItems([]);
    } finally {
      setLoadingExistingLogos(false);
    }
  };

  useEffect(() => {
    loadExistingPartnerLogos();
  }, [formData.name, formData.logoPath]);

  const loadAvailablePartners = async () => {
    try {
      console.log('🔍 Loading available partners...');
      const { data } = await client.models.Partner.list();
      console.log('📝 Available partners loaded:', data);
      
      const partners = (data || []).map((partner: any) => ({
        id: partner.id,
        name: partner.name || 'Unnamed Partner'
      }));
      
      setAvailablePartners(partners);
    } catch (error) {
      console.error('❌ Error loading available partners:', error);
      setAvailablePartners([]);
    }
  };

  const handlePartnerSelection = (partnerId: string) => {
    setSelectedPartnerId(partnerId);
    if (partnerId) {
      loadPartner(partnerId);
    } else {
      // Reset to create mode
      setFormData({
        name: '',
        logoPath: '',
        website: '',
        person: '',
        email: '',
        description: '',
        isActive: true,
        displayOrder: 0
      });
      // Clean up blob URL if it exists
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
      setLogoFile(null);
      setLogoPreview('');
    }
    setErrors({});
  };

  const loadPartner = async (id: string) => {
    setLoading(true);
    try {
      console.log('🔍 Loading partner with ID:', id);
      const { data } = await client.models.Partner.get({ id });
      console.log('📝 Partner loaded:', data);
      
      if (data) {
        setFormData({
          name: data.name || '',
          logoPath: data.logoPath || '',
          website: data.website || '',
          person: data.person || '',
          email: data.email || '',
          description: data.description || '',
          isActive: data.isActive ?? true,
          displayOrder: data.displayOrder ?? 0
        });

        // Preview will be resolved by the logoPath resolver effect
      }
    } catch (error) {
      console.error('❌ Error loading partner:', error);
      setMessage({ type: 'error', text: 'Failed to load partner. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Only validate format constraints, no required fields
    if (formData.logoPath && !formData.logoPath.endsWith('.svg')) {
      newErrors.logoPath = 'Logo must be an SVG file (.svg)';
    }

    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('svg') && !file.name.toLowerCase().endsWith('.svg')) {
      setErrors(prev => ({ ...prev, logoFile: 'Please select an SVG file' }));
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      setErrors(prev => ({ ...prev, logoFile: 'File size must be less than 1MB' }));
      return;
    }

    setLogoFile(file);
    setErrors(prev => ({ ...prev, logoFile: '' }));

    // Set for local preview; actual path will be set on upload

    // Create preview for SVG
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      // For SVG files, create a data URL or use the text content
      if (file.type.includes('svg') || file.name.toLowerCase().endsWith('.svg')) {
        // Create a blob URL for SVG files
        const blob = new Blob([result], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        setLogoPreview(url);
      } else {
        setLogoPreview(result);
      }
    };
    // Read as text for SVG files to handle them properly
    if (file.type.includes('svg') || file.name.toLowerCase().endsWith('.svg')) {
      reader.readAsText(file);
    } else {
      reader.readAsDataURL(file);
    }
  };

  const clearLogo = () => {
    // Clean up blob URL if it exists
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    
    setLogoFile(null);
    setLogoPreview('');
    handleInputChange('logoPath', '');
    // Clear the file input using ref
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'isActive' ? value === 'true' : field === 'displayOrder' ? parseInt(value) || 0 : value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (saving) {
      console.warn('⚠️ Save already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      console.log('🔧 Partner submission started');
      
      // Prepare partner data - include all fields including optional ones
      const partnerData = {
        name: formData.name.trim(),
        logoPath: formData.logoPath.trim(),
        website: formData.website.trim(),
        person: formData.person.trim(),
        email: formData.email.trim(),
        description: formData.description.trim(),
        isActive: formData.isActive,
        displayOrder: formData.displayOrder
      };
      
      console.log('📝 Creating/updating partner with data:', partnerData);

      let result;
      if (isEditMode && selectedPartnerId) {
        // Update existing partner
        result = await client.models.Partner.update({
          id: selectedPartnerId,
          ...partnerData
        });
        console.log('✅ Partner update result:', result);
      } else {
        // Create new partner
        result = await client.models.Partner.create(partnerData);
        console.log('✅ Partner creation result:', result);
      }

      if (result?.data) {
        const savedPartner: Partner = {
          id: result.data.id,
          name: result.data.name || '',
          logoPath: result.data.logoPath || '',
          person: result.data.person || '',
          email: result.data.email || '',
          description: result.data.description || '',
          createdAt: result.data.createdAt || new Date().toISOString(),
          updatedAt: result.data.updatedAt || new Date().toISOString()
        };

        if (onSave) {
          onSave(savedPartner);
        }

        // Reload available partners list to update the selector
        await loadAvailablePartners();

        if (!isEditMode) {
          // Reset form for new partner
          setFormData({
            name: '',
            logoPath: '',
            website: '',
            person: '',
            email: '',
            description: '',
            isActive: true,
            displayOrder: 0
          });
          // Clean up blob URL if it exists
          if (logoPreview && logoPreview.startsWith('blob:')) {
            URL.revokeObjectURL(logoPreview);
          }
          setLogoFile(null);
          setLogoPreview('');
        }

        setMessage({ type: 'success', text: `Partner ${isEditMode ? 'updated' : 'created'} successfully!` });
      } else {
        throw new Error('No data returned from server');
      }
    } catch (err: any) {
      console.error('❌ Failed to save partner:', {
        name: (err as any)?.name,
        message: (err as any)?.message,
        stack: (err as any)?.stack,
        errors: (err as any)?.errors
      });
      
      let errorMessage = 'Failed to save partner. Please try again.';
      if ((err as any)?.errors?.[0]?.message) {
        errorMessage = `GraphQL error: ${(err as any).errors[0].message}`;
      } else if ((err as any)?.message) {
        errorMessage = `Error: ${(err as any).message}`;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Always clear the form to blank values
    setFormData({
      name: '',
      logoPath: '',
      website: '',
      person: '',
      email: '',
      description: '',
      isActive: true,
      displayOrder: 0
    });
    // Clean up blob URL if it exists
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(null);
    setLogoPreview('');
    setErrors({});
  };

  // Inject selector into header
  React.useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      container.innerHTML = `
        <select
          id="partnerSelect"
          style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
          ${saving ? 'disabled' : ''}
        >
          <option value="" ${selectedPartnerId === '' ? 'selected' : ''}>Create New Partner</option>
          ${availablePartners.map(partner => 
            `<option value="${partner.id}" ${selectedPartnerId === partner.id ? 'selected' : ''}>${partner.name}</option>`
          ).join('')}
        </select>
      `;

      // Add event listeners
      const selectElement = container.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', (e) => {
          handlePartnerSelection((e.target as HTMLSelectElement).value);
        });
      }
    }

    return () => {
      // Cleanup
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [selectedPartnerId, availablePartners, saving]);

  if (loading) {
    return (
      <div className="admin-form">
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div>Loading partner...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-form admin-editor-content">
      {/* Saving Overlay */}
      {saving && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          cursor: 'wait'
        }}>
          <div style={{
            background: 'white',
            padding: '24px 32px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #008F99',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>
              Saving partner...
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Please do not close this window
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      <form onSubmit={handleSubmit} style={{ pointerEvents: saving ? 'none' : 'auto', opacity: saving ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="name">Partner Name</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter partner name"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && (
              <div className="error-message">{errors.name}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="website">Website URL</label>
            <input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder="https://www.example.com"
            />
          </div>

          <div className="form-field" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="logoFile">Partner Logo</label>
            
            {/* Three-column layout: Current Logo | Logo Controls | Empty */}
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '16px 0' }}>
              <tbody>
                <tr>
                  {/* Left column: Current Logo Preview */}
                  <td style={{ verticalAlign: 'top', width: '240px' }}>
                    {logoPreview && (
                      <div style={{
                        padding: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        background: '#f9fafb',
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                          Current Logo
                        </div>
                        <div style={{
                          width: '200px',
                          height: '120px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <img
                            src={logoPreview}
                            alt="Current logo"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              console.error('Failed to load logo preview:', logoPreview);
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                            onLoad={() => {
                              console.log('Logo preview loaded successfully:', logoPreview);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </td>

                  {/* Center column: Logo Controls (30% width) */}
                  <td style={{ verticalAlign: 'top', width: '30%' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Existing S3 logos selector */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select
                          value={formData.logoPath || ''}
                          onChange={(e) => handleInputChange('logoPath', e.target.value)}
                          style={{ flex: 1 }}
                        >
                          <option value="">Choose existing partner logo…</option>
                          {existingLogoItems.map(item => (
                            <option key={item.key} value={item.key}>{item.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="admin-btn"
                          onClick={loadExistingPartnerLogos}
                          disabled={loadingExistingLogos}
                        >
                          {loadingExistingLogos ? 'Refreshing…' : 'Refresh list'}
                        </button>
                      </div>

                      {/* Choose File button */}
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <input
                          ref={fileInputRef}
                          id="partner-logo-upload"
                          type="file"
                          accept=".svg,image/svg+xml"
                          onChange={handleFileUpload}
                          disabled={saving}
                          style={{ display: 'none' }}
                        />
                        <label
                          htmlFor="partner-logo-upload"
                          style={{
                            padding: '8px 16px',
                            border: '1px solid #008F99',
                            borderRadius: '6px',
                            background: 'white',
                            color: '#008F99',
                            fontSize: '14px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            flex: 1,
                            opacity: saving ? 0.6 : 1,
                            display: 'inline-block',
                            textAlign: 'center',
                            userSelect: 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (!saving) {
                              e.currentTarget.style.background = '#008F99';
                              e.currentTarget.style.color = 'white';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!saving) {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.color = '#008F99';
                            }
                          }}
                        >
                          {logoFile ? `Selected: ${logoFile.name}` : 'Choose File'}
                        </label>
                        {(logoFile || logoPreview) && (
                          <button
                            type="button"
                            onClick={clearLogo}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #dc2626',
                              borderRadius: '6px',
                              background: 'white',
                              color: '#dc2626',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#dc2626';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.color = '#dc2626';
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>

                      {/* Upload button */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          type="button"
                          className="admin-btn"
                          disabled={!logoFile || logoUploading}
                          onClick={async () => {
                            if (!logoFile) return;
                            try {
                              setLogoUploading(true);
                              const safeSlug = (formData.name || 'partner')
                                .toLowerCase()
                                .replace(/[^a-z0-9\-]+/g, '-')
                                .replace(/^-+|-+$/g, '');
                              const timestamp = Date.now();
                              const key = `partners/${safeSlug}/${timestamp}_${logoFile.name}`;
                              await uploadData({ key, data: logoFile, options: { contentType: logoFile.type } }).result;
                              const { url } = await getUrl({ path: `public/${key}` });
                              handleInputChange('logoPath', key);
                              setLogoPreview(url.toString());
                              setMessage({ type: 'success', text: 'Logo uploaded successfully.' });
                              loadExistingPartnerLogos();
                            } catch (err:any) {
                              console.error('Logo upload failed:', err);
                              setMessage({ type: 'error', text: `Logo upload failed: ${err?.message || err}` });
                            } finally {
                              setLogoUploading(false);
                            }
                          }}
                        >
                          {logoUploading ? 'Uploading…' : 'Upload'}
                        </button>
                      </div>
                    </div>
                  </td>

                  {/* Right column: Empty */}
                  <td style={{ verticalAlign: 'top' }}>
                    {/* Empty column */}
                  </td>
                </tr>
              </tbody>
            </table>
            
            {errors.logoFile && (
              <div className="error-message">{errors.logoFile}</div>
            )}
            {errors.logoPath && (
              <div className="error-message">{errors.logoPath}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="person">Contact Person</label>
            <input
              id="person"
              type="text"
              value={formData.person}
              onChange={(e) => handleInputChange('person', e.target.value)}
              placeholder="Enter contact person name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="contact@partner.com"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && (
              <div className="error-message">{errors.email}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Brief description of the partner..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div className="form-field">
            <label htmlFor="displayOrder">Display Order</label>
            <input
              type="number"
              id="displayOrder"
              value={formData.displayOrder}
              onChange={(e) => handleInputChange('displayOrder', e.target.value)}
              placeholder="0"
              min="0"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Lower numbers appear first in listings
            </small>
          </div>

          <div className="form-field">
            <label htmlFor="isActive" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked.toString())}
                style={{ margin: 0 }}
              />
              Active Partner
            </label>
            <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Only active partners are displayed publicly
            </small>
          </div>
        </div>



        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="admin-btn secondary"
            disabled={saving}
          >
            Reset
          </button>
        </div>
      </form>
      
      <StickyFooter
        onSave={() => {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }}
        saving={saving}
        message={message}
        onMessageClose={() => setMessage(null)}
      />
    </div>
  );
};

export default AdminPartnerEditor;
